import { useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Pause, Play } from 'lucide-react';
import { playbackStartSeconds } from '../../lib/speedToLead/callMedia';
import { barPercent, clockLabel, seekTarget, shownTotal } from '../../lib/leads/recordingBar';
import type { CallLogRow } from '../../types/leadCall';

// THE RECORDING PLAY BAR (Leads centre v2, polished): a round play / pause button (an icon, no word), a progress bar you can click to seek, and
// elapsed over total in Space Mono. Replaces the browser's own audio controls.
//
// ⚠️⚠️ THE SIGNED URL IS FETCHED ON THE PRESS OF PLAY, through the host's existing scoped `loadPlayUrl`, and only THEN does an <audio>
// element exist. Before that press nothing in the page carries a URL (no src, no link, no preload). It is never put in the page
// address, never logged, and never kept after the row unmounts. This component makes NO request on render: every request is a click.
// ⚠️ The TOTAL shows before play, from the call's known duration (the audio is not loaded to learn it); the audio's own length takes over
// once it is playing. The stored file is untouched; playback starts after the hold silence (`recordingOffsetSeconds`) so the clock starts at
// the lead. Seeking is by clicking the bar (or the arrow keys on it) and only once the audio exists.

export interface RecordingBarProps {
  kind: CallLogRow['kind'];
  id: string;
  /** Seconds of hold silence before the lead joined. Null/absent = from the start. */
  offsetSeconds?: number | null;
  /** The call's known length in seconds, so the total reads before anything is loaded. */
  durationSeconds?: number | null;
  loadPlayUrl: (kind: CallLogRow['kind'], id: string) => Promise<string>;
}

const SEEK_STEP_SECONDS = 5;

export function RecordingBar({ kind, id, offsetSeconds, durationSeconds, loadPlayUrl }: RecordingBarProps) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [time, setTime] = useState({ now: 0, total: 0 });
  const start = playbackStartSeconds(offsetSeconds);

  const toggle = async () => {
    if (src && audio.current) {
      if (audio.current.paused) void audio.current.play().catch(() => setError('Could not play the recording.'));
      else audio.current.pause();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setSrc(await loadPlayUrl(kind, id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the recording.');
    } finally {
      setBusy(false);
    }
  };

  const total = shownTotal(time.total, start, durationSeconds);
  const elapsed = Math.max(0, time.now - start);
  const percent = barPercent(elapsed, total);

  const seekTo = (seconds: number) => {
    if (!audio.current) return;
    audio.current.currentTime = seconds;
    setTime((t) => ({ ...t, now: seconds }));
  };
  const onBarClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!src || !audio.current || time.total <= 0) return;
    const box = e.currentTarget.getBoundingClientRect();
    if (box.width <= 0) return;
    seekTo(seekTarget((e.clientX - box.left) / box.width, start, time.total));
  };
  const onBarKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!src || !audio.current || time.total <= 0) return;
    const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (dir === 0) return;
    e.preventDefault();
    seekTo(Math.min(time.total, Math.max(start, time.now + dir * SEEK_STEP_SECONDS)));
  };

  return (
    <div className="kc-recording flex items-center gap-3 rounded-full border border-border px-2 py-1.5" data-testid="recording-bar">
      <button
        type="button"
        className="kc-rec-play flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
        onClick={() => void toggle()}
        disabled={busy}
        aria-label={busy ? 'Loading the recording' : playing ? 'Pause recording' : 'Play recording'}
        data-testid="recording-toggle"
      >
        {playing ? <Pause size={16} strokeWidth={1.5} fill="currentColor" aria-hidden /> : <Play size={16} strokeWidth={1.5} fill="currentColor" aria-hidden />}
      </button>
      <div
        className="kc-rec-track h-1.5 min-w-[96px] flex-1 cursor-pointer overflow-hidden rounded-full bg-muted"
        role="slider"
        tabIndex={src ? 0 : -1}
        aria-label="Recording position"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-disabled={!src}
        onClick={onBarClick}
        onKeyDown={onBarKey}
        data-testid="recording-track"
      >
        <div className="kc-rec-fill h-full bg-foreground" style={{ width: `${percent}%` }} />
      </div>
      <span className="kc-label shrink-0 pr-1 text-xs tabular-nums text-muted-foreground" data-testid="recording-clock">
        {clockLabel(elapsed)} / {clockLabel(total)}
      </span>
      {src && (
        // The element exists only AFTER a press of Play, and carries the one signed URL; there are no native controls (this bar is the control).
        <audio
          ref={audio}
          src={src}
          autoPlay
          preload="auto"
          onLoadedMetadata={(e) => {
            if (start > 0) e.currentTarget.currentTime = start;
            setTime({ now: e.currentTarget.currentTime, total: Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0 });
          }}
          onTimeUpdate={(e) => setTime((t) => ({ ...t, now: e.currentTarget.currentTime }))}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
      )}
      {error && <span role="alert" className="text-xs text-destructive">{error}</span>}
    </div>
  );
}
